import { useCallback } from 'react';

import { Box, IconButton, Tooltip } from '@mui/material';

import isEmpty from 'lodash/isEmpty';

import MapPinIcon from '@/components/Icons/MapPinIcon';
import MapPinnedIcon from '@/components/Icons/MapPinnedIcon';
import { MAX_PINNED_IDS } from '@/constants';
import useIsMultipleTeamsEnabled from '@/hooks/useIsMultipleTeamsEnabled';
import useUser from '@/hooks/useUser';
import { WorkspaceSections } from '@/sections/Sidebar/types';
import { POLL_INTERVAL } from '@/sections/Workspace/hooks';
import { objectInsertIf } from '@/utils/collections/insertIf';
import { useSnackbar } from 'notistack';
import { useRecoilState } from 'recoil';

import { pinnedIdsState } from '../atoms';
import { sortPinnedIds } from '../utils';
import { useTrainingProjectsQuery } from './__generated__/queries.generated';

function useTrainingProjects(poll = false, teamId?: string) {
  const isMultipleTeamsEnabled = useIsMultipleTeamsEnabled();
  const { data, loading, refetch } = useTrainingProjectsQuery({
    fetchPolicy: 'cache-first',
    pollInterval: poll ? POLL_INTERVAL : null,
    ...objectInsertIf(teamId, { variables: { teamId } }),
    ...objectInsertIf(isMultipleTeamsEnabled && !teamId, { variables: { all: true } }),
  });

  return {
    trainingProjects: data?.trainingProjects || [],
    loading: isEmpty(data) && loading,
    refetch,
  };
}

function usePinnedAPI() {
  const user = useUser();
  const { enqueueSnackbar } = useSnackbar();
  const [pinnedIds, setPinnedIds] = useRecoilState(
    pinnedIdsState(`${WorkspaceSections.Training}-${user.id}`),
  );
  const { trainingProjects, loading } = useTrainingProjects();

  const pinnedTrainingProjects = trainingProjects
    .filter((trainingProject) => pinnedIds.includes(trainingProject.id))
    .sort((a, b) => sortPinnedIds(pinnedIds, a.id, b.id));

  const togglePinEntity = useCallback(
    (entityId: string, entityName: string) => {
      setPinnedIds((prev) => {
        if (prev.includes(entityId)) {
          return prev.filter((id) => id !== entityId);
        }
        enqueueSnackbar(`Pinned ${entityName} to the sidebar`, { variant: 'success' });
        return [entityId, ...prev];
      });
    },
    [setPinnedIds, enqueueSnackbar],
  );

  const canPinMoreTrainingProjects = pinnedTrainingProjects.length < MAX_PINNED_IDS;

  function stopPropagation(ev: React.MouseEvent) {
    ev.preventDefault();
    ev.stopPropagation();
  }

  function getPinActionItem(entityId: string, entityName: string) {
    const isPinned = pinnedIds.includes(entityId);
    const isPinDisabled = !canPinMoreTrainingProjects && !isPinned;

    return (
      <Tooltip
        title={
          isPinDisabled
            ? `Limit ${MAX_PINNED_IDS} pins`
            : isPinned
            ? 'Unpin from sidebar'
            : 'Pin to sidebar'
        }
      >
        <Box>
          <IconButton
            onClick={(e) => {
              stopPropagation(e);
              togglePinEntity(entityId, entityName);
            }}
            data-cy="pin-entity-btn"
            disabled={isPinDisabled}
          >
            {isPinned ? <MapPinnedIcon color="inherit" /> : <MapPinIcon color="secondary" />}
          </IconButton>
        </Box>
      </Tooltip>
    );
  }

  return {
    pinnedIds,
    togglePinEntity,
    canPinMoreTrainingProjects,
    getPinActionItem,
    pinnedTrainingProjects,
    loading,
  };
}

export { useTrainingProjects, usePinnedAPI };
